import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, ClipboardList, MessageSquare, Star, Users, type LucideIcon } from "lucide-react";

import { eventService } from "../services/eventService";
import { usePermissions } from "../hooks/usePermissions";
import { todayIso } from "../utils/dates";
import { EventsPanel } from "../components/events/EventsPanel";
import { FeedbackPanel } from "../components/events/FeedbackPanel";
import { RegistrationsPanel } from "../components/events/RegistrationsPanel";
import { TasksPanel } from "../components/events/TasksPanel";
import { canGiveFeedback, dateRange, isOpenTask, isWaiting, timeRange } from "../components/events/eventsLogic";

type Tab = "events" | "registrations" | "feedback" | "tasks";

/** School events: the calendar, registration and check-in, feedback, and the planning behind them. */
export default function Events() {
  const { hasCapability } = usePermissions();
  const canManage = hasCapability("access.core.academic_staff");
  const tabs: { id: Tab; label: string; icon: LucideIcon }[] = [
    { id: "events", label: "Events", icon: CalendarDays },
    { id: "registrations", label: canManage ? "Registrations" : "My registrations", icon: Users },
    { id: "feedback", label: "Feedback", icon: MessageSquare },
    ...(canManage ? [{ id: "tasks" as const, label: "Planning tasks", icon: ClipboardList }] : []),
  ];
  const [params, setParams] = useSearchParams();
  const tab: Tab = tabs.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "events";
  const view = params.get("view") ?? undefined;
  const select = (next: Tab, nextView?: string) => {
    const query: Record<string, string> = {};
    if (next !== "events") query.tab = next;
    if (nextView) query.view = nextView;
    setParams(query, { replace: true });
  };

  const queryClient = useQueryClient();
  const events = useQuery({ queryKey: ["events", "list"], queryFn: eventService.getEvents });
  const categories = useQuery({ queryKey: ["events", "categories"], queryFn: eventService.getCategories, enabled: canManage });
  const registrations = useQuery({ queryKey: ["events", "registrations"], queryFn: eventService.getRegistrations });
  const feedback = useQuery({ queryKey: ["events", "feedback"], queryFn: eventService.getFeedback });
  const tasks = useQuery({ queryKey: ["events", "tasks"], queryFn: eventService.getTasks, enabled: canManage });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["events"] });

  const today = todayIso();
  const rows = events.data ?? [];
  const upcoming = rows.filter((row) => row.status === "published" && row.end_date >= today).sort((a, b) => `${a.start_date}${a.start_time ?? ""}`.localeCompare(`${b.start_date}${b.start_time ?? ""}`));
  const next = upcoming[0];
  const drafts = rows.filter((row) => row.status === "draft").length;
  const toClose = rows.filter((row) => row.status === "published" && row.end_date < today).length;
  const openTasks = (tasks.data ?? []).filter(isOpenTask);
  const overdue = openTasks.filter((row) => row.due_date && row.due_date < today).length;
  const mine = (registrations.data ?? []).filter((row) => isWaiting(row) && row.event_end_date >= today && row.event_status !== "cancelled").length;
  const toRate = (registrations.data ?? []).filter((row) => canGiveFeedback(row, today)).length;
  const ratings = (feedback.data ?? []).map((row) => row.overall_rating);
  const average = ratings.length ? (ratings.reduce((sum, value) => sum + value, 0) / ratings.length).toFixed(1) : "—";
  const dash = (query: { isLoading: boolean }, text: string) => (query.isLoading ? "–" : text);

  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = [
    {
      label: "Next event",
      value: dash(events, next ? next.name : "None coming up"),
      note: next ? `${next.start_date === today ? "Today" : dateRange(next)}${timeRange(next) ? `, ${timeRange(next)}` : ""}` : canManage ? "Publish one to fill the calendar" : "Check back soon",
      go: () => select("events", "upcoming"),
      icon: CalendarDays,
    },
    canManage
      ? {
          label: "Needs attention",
          value: dash(events, String(drafts + toClose)),
          note: toClose ? `${toClose} register${toClose === 1 ? "" : "s"} to close` : drafts ? `${drafts} draft${drafts === 1 ? "" : "s"} to publish` : "All in order",
          go: () => select("events", toClose ? "close" : "drafts"),
          icon: CalendarDays,
          alert: toClose > 0,
        }
      : { label: "I'm registered for", value: dash(registrations, String(mine)), note: mine === 1 ? "event coming up" : "events coming up", go: () => select("registrations", "upcoming"), icon: Users },
    canManage
      ? { label: "Open tasks", value: dash(tasks, String(openTasks.length)), note: overdue ? `${overdue} overdue` : "None overdue", go: () => select("tasks", overdue ? "overdue" : "open"), icon: ClipboardList, alert: overdue > 0 }
      : { label: "Feedback to give", value: dash(registrations, String(toRate)), note: toRate ? "Say how the event went" : "Nothing waiting", go: () => select("registrations", "feedback"), icon: MessageSquare, alert: toRate > 0 },
    { label: canManage ? "Average rating" : "Feedback given", value: dash(feedback, canManage ? average : String(ratings.length)), note: canManage ? `From ${ratings.length} response${ratings.length === 1 ? "" : "s"}` : "Your responses", go: () => select("feedback"), icon: Star },
  ];
  const badges: Partial<Record<Tab, number>> = canManage ? { events: toClose, tasks: overdue } : { registrations: toRate };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Events</h1>
        <p className="mt-1 text-sm text-gray-500">
          {canManage
            ? "Plan and publish school events, see who is coming, check people in at the door and read what they thought."
            : "What is coming up at school, the events you registered for, and how they went."}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={tile.go}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Event sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => {
          const active = tab === item.id;
          const badge = badges[item.id];
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => select(item.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "events" && (
        <EventsPanel
          events={events.data}
          categories={categories.data ?? []}
          tasks={tasks.data ?? []}
          feedback={feedback.data ?? []}
          isLoading={events.isLoading}
          isError={events.isError}
          onRetry={() => events.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "registrations" && (
        <RegistrationsPanel
          registrations={registrations.data}
          isLoading={registrations.isLoading}
          isError={registrations.isError}
          onRetry={() => registrations.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "feedback" && <FeedbackPanel feedback={feedback.data} isLoading={feedback.isLoading} isError={feedback.isError} onRetry={() => feedback.refetch()} canManage={canManage} />}
      {tab === "tasks" && canManage && (
        <TasksPanel tasks={tasks.data} events={rows} isLoading={tasks.isLoading} isError={tasks.isError} onRetry={() => tasks.refetch()} onChanged={refresh} initialView={view} />
      )}
    </div>
  );
}
